// src/pages/promo/QuickDiagnosis.js
//
// Public, shareable promo page reachable at /free and /free/result/:leadId.
// Visitors enter email + target role + resume (paste or file) and get a
// teaser diagnosis: overall score and radar chart are visible; the dimension
// cards, top issues and 7-day fix plan are blurred behind a "Sign up to
// unlock" CTA. We persist a lead doc so registering with the same email
// later auto-recovers the parsed resume + cached diagnosis.

import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
} from 'recharts';
import {
  FiArrowRight, FiUpload, FiCheck, FiLock, FiMail, FiCheckCircle,
} from 'react-icons/fi';
import { promoApi } from '../../services/api';

const TEAL = '#5AAFA7';

const BREAKDOWN_ORDER = [
  'skill_coverage', 'clarity', 'impact_quantification',
  'structural_completeness', 'professional_relevance', 'narrative_strength',
];
const BREAKDOWN_LABELS = {
  skill_coverage: 'Skill Coverage',
  clarity: 'Clarity',
  impact_quantification: 'Impact',
  structural_completeness: 'Structure',
  professional_relevance: 'Relevance',
  narrative_strength: 'Narrative',
};

const LOADING_MSGS = [
  'Scanning your work experience…',
  'Checking keyword alignment with your target role…',
  'Comparing against market benchmarks…',
  'Identifying the top bottlenecks holding you back…',
  'Writing your diagnosis…',
];

function statusColor(status) {
  return status === 'green' ? '#10b981' : status === 'yellow' ? '#f59e0b' : '#ef4444';
}

// ── Score gauge (small, mobile-friendly) ───────────────────────────────────────

function ScoreGauge({ score = 0, status, size = 140 }) {
  const color = statusColor(status || (score >= 80 ? 'green' : score >= 60 ? 'yellow' : 'red'));
  const data = [
    { value: score, fill: color },
    { value: 100 - score, fill: '#f1f5f9' },
  ];
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius="58%"
            outerRadius="85%"
            dataKey="value"
            startAngle={90}
            endAngle={-270}
          >
            {data.map((e, i) => <Cell key={i} fill={e.fill} stroke="none" />)}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
      }}
      >
        <span style={{ fontSize: size * 0.26, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: size * 0.10, color: '#94a3b8' }}>/ 100</span>
      </div>
    </div>
  );
}

function StatusBadge({ status, label }) {
  const bg = status === 'green' ? '#dcfce7' : status === 'yellow' ? '#fef9c3' : '#fee2e2';
  const color = statusColor(status);
  const dot = status === 'green' ? '🟢' : status === 'yellow' ? '🟡' : '🔴';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
      background: bg, color,
    }}
    >
      {dot} {label}
    </span>
  );
}

function InsightRadar({ radarScores }) {
  if (!radarScores) return null;
  const data = BREAKDOWN_ORDER
    .filter(k => radarScores[k])
    .map(k => ({
      axis: BREAKDOWN_LABELS[k],
      value: Math.round((radarScores[k].score || 0) * 10),
    }));
  if (data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}>
        <PolarGrid stroke="#e2e8f0" />
        <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11, fill: '#64748b' }} />
        <Radar dataKey="value" stroke={TEAL} fill={TEAL} fillOpacity={0.35} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

// ── Blurred section wrapper ────────────────────────────────────────────────────

function BlurredPreview({ children }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'relative',
        filter: 'blur(8px)',
        pointerEvents: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        opacity: 0.85,
      }}
    >
      {children}
    </div>
  );
}

// Faux placeholders so the blurred area looks substantial.
function FauxDimGrid() {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10,
    }}
    >
      {BREAKDOWN_ORDER.map((k, i) => (
        <div
          key={k}
          style={{
            background: '#fff', borderRadius: 10, padding: '14px 16px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderTop: `3px solid ${TEAL}`,
            minHeight: 120,
          }}
        >
          <div style={{ height: 10, width: '60%', background: '#e2e8f0', borderRadius: 4, marginBottom: 10 }} />
          <div style={{ height: 26, width: 70, background: '#1e293b', borderRadius: 4, opacity: 0.85, marginBottom: 10 }} />
          <div style={{ height: 5, width: `${30 + (i * 11) % 70}%`, background: TEAL, borderRadius: 4, marginBottom: 10 }} />
          <div style={{ height: 8, width: '90%', background: '#e2e8f0', borderRadius: 4, marginBottom: 6 }} />
          <div style={{ height: 8, width: '70%', background: '#e2e8f0', borderRadius: 4 }} />
        </div>
      ))}
    </div>
  );
}

function FauxIssueList() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {[1, 2, 3].map(i => (
        <div
          key={i}
          style={{
            background: '#fff', borderRadius: 12, padding: '18px 20px',
            boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            borderLeft: `5px solid ${i === 1 ? '#ef4444' : '#f59e0b'}`,
          }}
        >
          <div style={{ height: 14, width: `${50 + i * 10}%`, background: '#1e293b', opacity: 0.85, borderRadius: 4, marginBottom: 10 }} />
          <div style={{ height: 10, width: '95%', background: '#e2e8f0', borderRadius: 4, marginBottom: 6 }} />
          <div style={{ height: 10, width: '78%', background: '#e2e8f0', borderRadius: 4, marginBottom: 6 }} />
          <div style={{ height: 10, width: '60%', background: '#e2e8f0', borderRadius: 4 }} />
        </div>
      ))}
    </div>
  );
}

function FauxFixPlan() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {[1, 2, 3].map(i => (
        <div
          key={i}
          style={{
            background: '#fff', borderRadius: 12, padding: '18px 20px',
            boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            borderTop: `4px solid ${i === 1 ? TEAL : '#6366f1'}`,
          }}
        >
          <div style={{ height: 14, width: `${40 + i * 10}%`, background: '#1e293b', opacity: 0.85, borderRadius: 4, marginBottom: 10 }} />
          <div style={{ height: 10, width: '88%', background: '#e2e8f0', borderRadius: 4, marginBottom: 6 }} />
          <div style={{ height: 10, width: '70%', background: '#e2e8f0', borderRadius: 4 }} />
        </div>
      ))}
    </div>
  );
}

// ── Stage 1: Form ──────────────────────────────────────────────────────────────

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

function FormStage({ onSubmit, submitting, error }) {
  const [email, setEmail] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [file, setFile] = useState(null);
  const [tab, setTab] = useState('paste');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  const wordCount = resumeText.trim().split(/\s+/).filter(Boolean).length;
  const fileReady = file && file.name;
  const resumeReady = (tab === 'paste' && wordCount >= 50) || (tab === 'upload' && fileReady);
  const emailValid = EMAIL_RE.test(email.trim());
  const isValid = emailValid && resumeReady;

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    if ((f.name || '').toLowerCase().endsWith('.txt')) {
      const reader = new FileReader();
      reader.onload = ev => {
        setResumeText(ev.target.result || '');
        setTab('paste');
      };
      reader.readAsText(f);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px 12px 40px', borderRadius: 10,
    border: '2px solid #e2e8f0', fontSize: 14, outline: 'none',
    boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.2s',
  };

  const submit = (e) => {
    e?.preventDefault();
    if (!isValid || submitting) return;
    onSubmit({
      email: email.trim().toLowerCase(),
      resumeText: tab === 'paste' ? resumeText : (file && file.name.toLowerCase().endsWith('.txt') ? resumeText : ''),
      file: tab === 'upload' && file && !file.name.toLowerCase().endsWith('.txt') ? file : null,
    });
  };

  return (
    <form onSubmit={submit}>
      <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1e293b', margin: '0 0 8px', lineHeight: 1.25 }}>
        Get your free Resume Diagnosis
      </h1>
      <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 24px' }}>
        We&apos;ll tell you exactly what&apos;s holding your job search back —
        not generic tips, your specific issues. <strong>60 seconds, no credit card.</strong>
      </p>

      <div style={{ marginBottom: 20, position: 'relative' }}>
        <FiMail
          size={16}
          style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}
        />
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="your@email.com"
          autoComplete="email"
          style={inputStyle}
          onFocus={e => (e.target.style.borderColor = TEAL)}
          onBlur={e => (e.target.style.borderColor = '#e2e8f0')}
        />
      </div>

      <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: 0 }}>
        {[['paste', '✏️  Paste Text'], ['upload', '📁  Upload File']].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            style={{
              padding: '8px 22px', fontSize: 13, fontWeight: tab === key ? 700 : 500,
              color: tab === key ? TEAL : '#64748b',
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: tab === key ? `2px solid ${TEAL}` : '2px solid transparent',
              marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        {tab === 'paste' ? (
          <>
            <textarea
              value={resumeText}
              onChange={e => setResumeText(e.target.value)}
              placeholder={'Paste your full resume here…\n\nInclude experience, education, skills, and any other sections.'}
              style={{
                width: '100%', minHeight: 220, padding: '14px 16px', fontSize: 13,
                border: `2px solid ${wordCount >= 50 ? TEAL : '#e2e8f0'}`, borderRadius: 10,
                outline: 'none', fontFamily: 'inherit', lineHeight: 1.6, resize: 'vertical',
                boxSizing: 'border-box', transition: 'border-color 0.2s',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>🔒 Private. We use this only to generate your diagnosis.</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: wordCount >= 50 ? TEAL : '#94a3b8' }}>
                {wordCount} / 50 words min
              </span>
            </div>
          </>
        ) : (
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => {
              e.preventDefault();
              setDragOver(false);
              handleFile(e.dataTransfer.files[0]);
            }}
            style={{
              border: `2px dashed ${dragOver ? TEAL : '#cbd5e1'}`, borderRadius: 12,
              padding: '40px 20px', textAlign: 'center',
              background: dragOver ? '#f0fdf4' : '#fafbff',
              cursor: 'pointer', transition: 'all 0.2s',
            }}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.docx,.doc,.txt,.html,.htm"
              onChange={e => handleFile(e.target.files[0])}
              style={{ display: 'none' }}
            />
            {fileReady ? (
              <p style={{ color: TEAL, fontWeight: 600, margin: 0 }}>
                <FiCheck style={{ verticalAlign: 'middle', marginRight: 6 }} />
                {file.name}
              </p>
            ) : (
              <>
                <FiUpload size={28} color="#94a3b8" />
                <p style={{ fontSize: 14, color: '#475569', margin: '10px 0 4px' }}>
                  Drop your file here, or <span style={{ color: TEAL, fontWeight: 600 }}>browse</span>
                </p>
                <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>PDF, DOCX, TXT supported</p>
              </>
            )}
          </div>
        )}
      </div>

      {error && (
        <p style={{
          marginTop: 16, padding: '10px 14px', borderRadius: 8,
          background: '#fee2e2', color: '#b91c1c', fontSize: 13, fontWeight: 500,
        }}
        >
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={!isValid || submitting}
        style={{
          marginTop: 20, width: '100%', padding: '14px', borderRadius: 10,
          background: isValid && !submitting ? TEAL : '#e2e8f0',
          color: isValid && !submitting ? '#fff' : '#94a3b8',
          fontSize: 15, fontWeight: 700, border: 'none',
          cursor: isValid && !submitting ? 'pointer' : 'not-allowed',
          transition: 'all 0.2s',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        {submitting ? 'Analyzing your resume…' : (
          <>
            Get my Free Diagnosis <FiArrowRight size={16} />
          </>
        )}
      </button>

      <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 12 }}>
        Already have an account? <Link to="/login" style={{ color: TEAL, fontWeight: 600 }}>Sign in</Link>
      </p>
    </form>
  );
}

// ── Stage 2: Loading ───────────────────────────────────────────────────────────

function LoadingStage({ msgIdx }) {
  const checks = ['Resume parsed', 'Market fit analyzed', 'Top issues identified'];
  return (
    <div style={{ textAlign: 'center', padding: '32px 0' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🩺</div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>
        Reading your resume…
      </h2>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 28 }}>
        We&apos;re doing what a recruiter does in 6 seconds — but we actually explain it.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, margin: '0 auto', maxWidth: 300 }}>
        {checks.map((msg, i) => {
          const done = msgIdx > i * 2;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                background: done ? TEAL : '#e2e8f0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, color: done ? '#fff' : '#94a3b8', transition: 'all 0.5s',
              }}
              >
                {done ? <FiCheckCircle size={14} /> : i + 1}
              </div>
              <span style={{
                fontSize: 13, color: done ? '#1e293b' : '#94a3b8',
                fontWeight: done ? 600 : 400,
              }}
              >
                {msg}
              </span>
            </div>
          );
        })}
      </div>

      <p style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic', marginTop: 28 }}>
        {LOADING_MSGS[msgIdx % LOADING_MSGS.length]}
      </p>
    </div>
  );
}

// ── Stage 3: Preview ───────────────────────────────────────────────────────────

function PreviewStage({ diagnosis, leadId, email }) {
  const {
    overall_score = 0,
    overall_status,
    overall_label,
    radar_scores = {},
  } = diagnosis || {};

  const params = new URLSearchParams({
    from: 'promo',
    ...(leadId ? { lead_id: leadId } : {}),
    ...(email ? { email } : {}),
  });
  const registerHref = `/register?${params.toString()}`;
  const loginHref = `/login?${params.toString()}`;

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <span style={{
          display: 'inline-block', padding: '4px 12px', borderRadius: 20,
          background: '#e6f7f6', color: TEAL, fontSize: 11, fontWeight: 700,
          letterSpacing: '0.05em', textTransform: 'uppercase',
        }}
        >
          Your Diagnosis is Ready
        </span>
      </div>
      <h2 style={{ fontSize: 24, fontWeight: 800, color: '#1e293b', textAlign: 'center', margin: '8px 0 24px' }}>
        🩺 Your Resume Health Report
      </h2>

      <div style={{
        background: '#fff', borderRadius: 14, padding: 24,
        boxShadow: '0 2px 10px rgba(0,0,0,0.07)', marginBottom: 20,
        display: 'grid', gridTemplateColumns: '1fr', gap: 16,
      }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          justifyContent: 'center',
        }}
        >
          <ScoreGauge score={overall_score} status={overall_status} size={140} />
          <div style={{ flex: '1 1 220px', minWidth: 180 }}>
            <StatusBadge status={overall_status} label={overall_label || 'Diagnosis'} />
            <h3 style={{ fontSize: 30, fontWeight: 800, color: '#1e293b', margin: '8px 0 4px' }}>
              {overall_score} / 100
            </h3>
            <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
              Weighted across 6 resume dimensions: skill coverage, impact, clarity,
              structure, professional relevance, and narrative.
            </p>
          </div>
        </div>

        <div style={{ width: '100%', maxWidth: 360, margin: '0 auto' }}>
          <InsightRadar radarScores={radar_scores} />
        </div>
      </div>

      {/* Locked content — blurred and overlaid with CTA */}
      <div style={{ position: 'relative' }}>
        <BlurredPreview>
          <div style={{
            background: '#fff', borderRadius: 14, padding: 24,
            boxShadow: '0 2px 10px rgba(0,0,0,0.07)', marginBottom: 16,
          }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', margin: '0 0 4px' }}>
              Your 6 Dimension Scores
            </h3>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 14px' }}>
              Every dimension explained, with concrete evidence from your resume.
            </p>
            <FauxDimGrid />
          </div>

          <div style={{
            background: '#fff', borderRadius: 14, padding: 24,
            boxShadow: '0 2px 10px rgba(0,0,0,0.07)', marginBottom: 16,
          }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', margin: '0 0 4px' }}>
              🔍 Top 3 Issues — Found in YOUR Resume
            </h3>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 14px' }}>
              Ranked by impact on interview probability.
            </p>
            <FauxIssueList />
          </div>

          <div style={{
            background: '#fff', borderRadius: 14, padding: 24,
            boxShadow: '0 2px 10px rgba(0,0,0,0.07)', marginBottom: 16,
          }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', margin: '0 0 4px' }}>
              📅 Next 7 Days Fix Plan
            </h3>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 14px' }}>
              Stop applying. Fix these first.
            </p>
            <FauxFixPlan />
          </div>
        </BlurredPreview>

        {/* Sticky CTA card overlaying the blurred section */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: '24px 16px',
          pointerEvents: 'none',
        }}
        >
          <div style={{
            position: 'sticky', top: 24,
            background: 'linear-gradient(135deg, #1e3a5f, #0f766e)',
            borderRadius: 16, padding: '24px 20px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            color: '#fff', textAlign: 'center', maxWidth: 460, width: '100%',
            pointerEvents: 'auto',
          }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'rgba(255,255,255,0.18)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 12,
            }}
            >
              <FiLock size={20} />
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px', lineHeight: 1.3 }}>
              Sign up free to unlock your full diagnosis
            </h3>
            <p style={{ fontSize: 13, color: '#b2f0e8', margin: '0 0 16px', lineHeight: 1.5 }}>
              See all 6 dimension scores, the top 3 issues found in your resume, and your
              7-day fix plan. We&apos;ll save your resume + this diagnosis to your account.
            </p>
            <Link
              to={registerHref}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '12px 22px', borderRadius: 10,
                background: '#fff', color: '#0f766e',
                textDecoration: 'none', fontWeight: 800, fontSize: 14,
              }}
            >
              Unlock Full Report <FiArrowRight size={14} />
            </Link>
            <p style={{ fontSize: 11, color: '#b2f0e8', marginTop: 12, marginBottom: 0 }}>
              Already have an account?{' '}
              <Link to={loginHref} style={{ color: '#fff', textDecoration: 'underline', fontWeight: 600 }}>
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page header ────────────────────────────────────────────────────────────────

function Header() {
  return (
    <header style={{
      borderBottom: '1px solid #e2e8f0', padding: '14px 0', background: '#fff',
      position: 'sticky', top: 0, zIndex: 10,
    }}
    >
      <div style={{
        maxWidth: 1100, margin: '0 auto', padding: '0 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}
      >
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <img src="/logo.svg" alt="Resumemo" style={{ height: 32 }} />
          <span style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>Resumemo</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link to="/login" style={{
            fontSize: 13, fontWeight: 600, color: '#64748b',
            textDecoration: 'none', padding: '8px 14px',
          }}
          >
            Sign in
          </Link>
          <Link to="/register" style={{
            fontSize: 13, fontWeight: 700, color: '#fff', background: TEAL,
            textDecoration: 'none', padding: '8px 16px', borderRadius: 8,
          }}
          >
            Sign up
          </Link>
        </div>
      </div>
    </header>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────

export default function QuickDiagnosis() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const sharedLeadId = params.leadId || null;

  const [stage, setStage] = useState(sharedLeadId ? 'loading' : 'form');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [diagnosis, setDiagnosis] = useState(null);
  const [leadId, setLeadId] = useState(sharedLeadId);
  const [email, setEmail] = useState('');
  const [msgIdx, setMsgIdx] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  // Drive the loading-message rotation while in `loading` stage.
  useEffect(() => {
    if (stage === 'loading') {
      intervalRef.current = setInterval(() => setMsgIdx(i => i + 1), 1500);
      return () => clearInterval(intervalRef.current);
    }
    return undefined;
  }, [stage]);

  // If we landed on /free/result/:leadId, fetch the cached diagnosis.
  useEffect(() => {
    if (!sharedLeadId) return;
    (async () => {
      try {
        const res = await promoApi.getDiagnosis(sharedLeadId);
        setDiagnosis(res.data);
        setLeadId(sharedLeadId);
        setStage('preview');
      } catch (err) {
        console.error('Failed to load shared diagnosis:', err);
        setError('We could not find that diagnosis. Try generating a new one.');
        setStage('form');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedLeadId]);

  const handleSubmit = async ({ email: em, targetRole, resumeText, file }) => {
    setSubmitting(true);
    setError('');
    setStage('loading');
    setEmail(em);
    try {
      const res = await promoApi.diagnose({
        email: em, targetRole, resumeText, file,
      });
      const data = res.data || {};
      setDiagnosis(data);
      setLeadId(data.lead_id || null);
      // Update URL so users can share or come back to the result.
      if (data.lead_id) {
        navigate(`/free/result/${data.lead_id}`, {
          replace: true,
          state: { diagnosis: data, email: em },
        });
      }
      setStage('preview');
    } catch (err) {
      console.error('Promo diagnose failed:', err);
      const msg = err?.response?.data?.detail
        || err?.message
        || 'Something went wrong. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Something went wrong. Please try again.');
      setStage('form');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <Header />

      <main style={{
        maxWidth: stage === 'preview' ? 880 : 640,
        margin: '0 auto', padding: '32px 16px 80px',
      }}
      >
        <div style={{
          background: stage === 'preview' ? 'transparent' : '#fff',
          borderRadius: 14,
          padding: stage === 'preview' ? 0 : '28px 24px',
          boxShadow: stage === 'preview' ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
        }}
        >
          {stage === 'form' && (
            <FormStage onSubmit={handleSubmit} submitting={submitting} error={error} />
          )}
          {stage === 'loading' && <LoadingStage msgIdx={msgIdx} />}
          {stage === 'preview' && diagnosis && (
            <PreviewStage diagnosis={diagnosis} leadId={leadId} email={email || (location.state?.email || '')} />
          )}
        </div>
      </main>

      <footer style={{
        textAlign: 'center', padding: '20px 16px 40px', color: '#94a3b8', fontSize: 12,
      }}
      >
        © 2026 Resumemo AI ·
        <Link to="/privacy-policy" style={{ color: '#94a3b8', marginLeft: 6, marginRight: 6 }}>Privacy</Link>·
        <Link to="/terms" style={{ color: '#94a3b8', marginLeft: 6 }}>Terms</Link>
      </footer>
    </div>
  );
}
